import {WarningOutlined} from '@ant-design/icons';
import {StopOutlined} from '@ant-design/icons/lib';
import {Space, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  AccountFieldsFragment,
  AccountStatus,
  SearchableAccountFilterInput,
  useAccountsQuery
} from 'api/generated';
import {Ellipsize, Favicon, Hint, TableCard} from 'components';
import {FilterList} from 'components/FilterList';
import deepEqual from 'deep-equal';
import {useCountries} from 'hooks/useCountries';
import {colors} from 'lib/colors';
import {ACCOUNT_FILTERS} from 'lib/enums';
import {formatEmail, formatRelativeDate, formatWebsite} from 'lib/formaters';
import {accountStatusLabels} from 'lib/labels';
import {omitEmpty} from 'lib/omitEmpty';
import {sortObjectKeysByValue, truncate} from 'lib/utils';
import React from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {AccountFilters} from 'routes/Accounts/AccountFilters';
import {GMV} from 'routes/Accounts/GMV';
import {FilterQueryParams} from 'routes/Users/UserFilters';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams
} from 'use-query-params';
import {useAccessLevel} from '../../hooks/useAccessLevel';

const ANY = 'ANY';

const tabList = [
  {
    key: AccountStatus.ACTIVE,
    tab: (
      <Hint
        icon={null}
        message="The account is live and it has processed at least one customer transaction">
        {accountStatusLabels[AccountStatus.ACTIVE]}
      </Hint>
    )
  },
  {
    key: AccountStatus.APPROVED,
    tab: (
      <Hint
        icon={null}
        message="The account has been approved (it is live), but hasn't processed any transactions yet">
        {accountStatusLabels[AccountStatus.APPROVED]}
      </Hint>
    )
  },
  {
    key: AccountStatus.PENDING_APPROVAL,
    tab: (
      <Hint icon={null} message="The account has submitted all the data but was not approved yet">
        {accountStatusLabels[AccountStatus.PENDING_APPROVAL]}
      </Hint>
    )
  },
  {
    key: AccountStatus.PENDING_REVIEW,
    tab: (
      <Hint
        icon={null}
        message="The account has requested a review after providing additional data">
        {accountStatusLabels[AccountStatus.PENDING_REVIEW]}
      </Hint>
    )
  },
  {
    key: AccountStatus.NOT_CONFIGURED,
    tab: (
      <Hint
        icon={null}
        message="The account is already configured but requires additional information">
        {accountStatusLabels[AccountStatus.NOT_CONFIGURED]}
      </Hint>
    )
  },
  {
    key: AccountStatus.PENDING_CONTRACT,
    tab: (
      <Hint
        icon={null}
        message="The account needs either business details, representative verification or contract signature.">
        {accountStatusLabels[AccountStatus.PENDING_CONTRACT]}
      </Hint>
    )
  },
  {
    key: ANY,
    tab: 'All'
  }
];

export const queryFiltersSchema = {
  id: StringParam,
  name: StringParam,
  adminEmail: StringParam,
  secret: StringParam,
  apiKey: StringParam,
  businessCountry: StringParam,
  businessLegalName: StringParam,
  businessCompanyName: StringParam,
  businessDocumentNumber: StringParam,
  businessEmail: StringParam,
  businessWebsite: StringParam,
  billingPlan: StringParam,
  referralCode: StringParam,
  promoCode: StringParam,
  dynamicBilling: BooleanParam,
  payoutTermsSuspended: BooleanParam,
  masterAccountId: StringParam,
  churned: BooleanParam,
  blocked: BooleanParam,
  tags: StringParam,
  riskProfile: StringParam,
  accountExecutive: StringParam,
  representativeName: StringParam,
  representativeDocumentNumber: StringParam
};

const textFields = [
  'name',
  'businessCompanyName',
  'businessLegalName',
  'adminEmail',
  'businessEmail',
  'businessWebsite',
  'representativeDocumentNumber'
];
const booleanFields = ['dynamicBilling', 'payoutTermsSuspended', 'churned', 'blocked'];

const getQueryType = (key: string) => {
  if (textFields.includes(key)) {
    return 'matchPhrase';
  }
  if (booleanFields.includes(key)) {
    return 'eq';
  }
  return 'match';
};

const buildFilter = (
  queryFilters: {[key: string]: any},
  status: string,
  masterAccountId: string
): SearchableAccountFilterInput => {
  const filter: SearchableAccountFilterInput = {};
  if (masterAccountId) {
    filter.masterAccountId = {match: masterAccountId};
  }
  if (status !== ANY) filter.status = {match: status};
  Object.entries(queryFilters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const queryType = getQueryType(key);
    (filter as any)[key] = {[queryType]: value};
  });
  return filter;
};

export const Accounts: React.FC<RouteComponentProps<{}> & {masterAccountId: string}> = ({
  history,
  masterAccountId
}) => {
  const [_size, setSize] = useQueryParam('size', NumberParam);
  const [_from, setFrom] = useQueryParam('from', NumberParam);
  const [_status, setStatus] = useQueryParam('status', StringParam);
  const [queryFilters, setQueryFilters] = useQueryParams(queryFiltersSchema);

  const size = _size || 20;
  const from = _from || 0;
  const status = _status || ANY;

  const {getCountryName} = useCountries();
  const {isAdmin} = useAccessLevel();
  const filter = buildFilter(queryFilters, status, masterAccountId);
  const {data, loading, refetch, previousData} = useAccountsQuery({
    variables: {from, size, filter}
  });

  const payload = data ?? previousData;
  const accounts = payload?.accounts.items || [];
  const total = payload?.accounts.total || undefined;

  const columns: Array<ColumnProps<AccountFieldsFragment>> = [
    {
      key: 'favicon',
      dataIndex: ['business', 'website'],
      className: 'minimized-cell',
      onCell: (account) => ({
        onClick() {
          history.push({
            pathname: `/accounts/${account.id}`,
            state: {fromMasterAccount: !!masterAccountId}
          });
        }
      }),
      render(website: string, account) {
        return <Favicon account={account} shape="square" />;
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      onCell: (account) => ({
        onClick(e) {
          e.preventDefault();
          const url = `/accounts/${account.id}`;
          if (e.ctrlKey || e.metaKey) {
            window.open(url, account.id)?.focus();
          } else {
            history.push({
              pathname: url,
              state: {fromMasterAccount: !!masterAccountId}
            });
          }
        }
      }),
      render(name: string, account: AccountFieldsFragment) {
        return (
          <Space>
            {truncate(name, 35)}
            {account.blocked ? (
              <Hint
                message="Blocked account"
                style={{marginLeft: 4}}
                icon={<StopOutlined style={{color: colors.red}} />}
              />
            ) : (
              account.churned && (
                <Hint
                  message="Churned account"
                  style={{marginLeft: 4}}
                  icon={<WarningOutlined style={{color: colors.red}} />}
                />
              )
            )}
          </Space>
        );
      }
    },
    {
      title: 'Email',
      dataIndex: 'adminEmail',
      className: 'minimized-cell',
      render: (email) => (
        <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>{formatEmail(email)}</Ellipsize>
      )
    },
    {
      title: 'Website',
      dataIndex: ['business', 'website'],
      className: 'minimized-cell',
      render: (website) => (
        <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>
          {formatWebsite(website)}
        </Ellipsize>
      )
    },
    {
      title: 'Country',
      className: 'minimized-cell',
      dataIndex: ['business', 'country'],
      render(countryCode: string) {
        return getCountryName(countryCode);
      }
    },
    {
      title: 'Plan',
      dataIndex: 'billingPlan',
      className: 'minimized-cell',
      render: (plan: string) => {
        return plan ? plan.replace(/_/g, ' ') : null;
      }
    },
    {
      title: 'GMV',
      dataIndex: 'id',
      align: 'right',
      width: 100,
      className: 'minimized-cell',
      render(id: string, account) {
        if (account.status !== AccountStatus.ACTIVE) return null;
        return <GMV accountId={id} />;
      }
    },
    {
      title: 'Last update',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      defaultSortOrder: 'descend',
      render(_, account) {
        const date = account.updatedAt || account.createdAt;
        if (!date) return null;
        return formatRelativeDate(date);
      }
    }
  ];

  const handleStatusChange = (status: string) => {
    setFrom(null, 'pushIn');
    setStatus(status, 'pushIn');
  };

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) return refetch();
    setQueryFilters(values, 'pushIn');
  };

  const handleCloseTag = (key: string) => setQueryFilters({...queryFilters, [key]: null}, 'pushIn');

  let availableFilters: Record<string, string> = {...ACCOUNT_FILTERS};

  let keysToRemove: string[] = [];

  // Remove filters that are only available for admins
  if (!isAdmin) {
    keysToRemove.push('tags');
  }

  // Remove filters that are not available for the current user
  if (masterAccountId) {
    keysToRemove.push('masterAccountId');
  }

  availableFilters = Object.fromEntries(
    Object.entries(availableFilters).filter(([key]) => !keysToRemove.includes(key))
  );

  availableFilters = sortObjectKeysByValue(availableFilters);

  return (
    <>
      <AccountFilters
        onChange={handleFiltersChange}
        queryFilters={queryFilters}
        availableFilters={availableFilters}
      />
      <FilterList
        queryFilters={queryFilters}
        filters={availableFilters}
        onClose={handleCloseTag}
        defaultQuery={masterAccountId ? '?tab=accounts' : '?status=ACTIVE'}
      />
      <TableCard
        size="small"
        tabList={tabList}
        activeTabKey={status!}
        onTabChange={handleStatusChange}>
        <Table
          scroll={{x: true}}
          dataSource={accounts}
          columns={columns}
          loading={loading}
          rowClassName={(account) => {
            if (account.churned) return 'clickable grayed-out';
            return 'clickable';
          }}
          rowKey="id"
          pagination={{
            pageSize: size,
            current: from / size + 1,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total.toLocaleString()} accounts`,
            onChange: (page, size = 0) => setFrom((page - 1) * size, 'pushIn'),
            onShowSizeChange: (page, size) => setSize(size, 'pushIn')
          }}
        />
      </TableCard>
    </>
  );
};
